import React, { useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    MenuItem,
    Paper,
    Select,
    Step,
    StepLabel,
    Stepper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { format } from 'util';
import { IGroup } from '../../../models/IEvent';
import IEditableRider from '../../../models/IEditableRider';
import { IDistributionGroup, IRidersDistribution } from '../../../models/IRidersDistribution';
import EventServiceInstance from '../../../services/EventService';

interface IRidersDistributionDialogProps {
    raceId: string;
    existingGroups: IGroup[];
    existingRiders: IEditableRider[];
    onApply: (updatedRiders: IEditableRider[], updatedGroups: IGroup[]) => void;
    onCancel: () => void;
}

function parseTimeToTicks(timeString: string): number {
    const colonIdx = timeString.lastIndexOf(':');
    if (colonIdx === -1) return 0;
    const minutes = parseInt(timeString.substring(0, colonIdx), 10) || 0;
    const seconds = parseFloat(timeString.substring(colonIdx + 1)) || 0;
    return Math.round((minutes * 60 + seconds) * 10000000);
}

function RidersDistributionDialog(props: IRidersDistributionDialogProps) {
    const { t } = useTranslation("common");
    const [activeStep, setActiveStep] = useState(0);
    const [numberOfGroups, setNumberOfGroups] = useState(
        props.existingGroups.length > 0 ? props.existingGroups.length : 2
    );
    const [distribution, setDistribution] = useState<IRidersDistribution | null>(null);
    const [loading, setLoading] = useState(false);
    // groupMappings: proposedGroupNumber -> existingGroupId
    const [groupMappings, setGroupMappings] = useState<Record<number, string>>({});
    // applyTargetTime: proposedGroupNumber -> boolean
    const [applyTargetTime, setApplyTargetTime] = useState<Record<number, boolean>>({});
    // riderGroups: tagId -> groupId (current working assignment)
    const [riderGroups, setRiderGroups] = useState<Record<string, string>>({});
    const [showOnlyMismatched, setShowOnlyMismatched] = useState(false);

    const steps = [
        t("distribution.stepFetch"),
        t("distribution.stepMapGroups"),
        t("distribution.stepReviewRiders"),
    ];

    const handleFetch = async () => {
        setLoading(true);
        const source = axios.CancelToken.source();
        try {
            const result = await EventServiceInstance.getRidersDistribution(
                props.raceId, numberOfGroups, source.token
            );
            if (result && result.groups.length > 0) {
                setDistribution(result);
                const initialMappings: Record<number, string> = {};
                const initialApplyTime: Record<number, boolean> = {};
                result.groups.forEach((g: IDistributionGroup, i: number) => {
                    initialMappings[g.groupNumber] =
                        i < props.existingGroups.length ? props.existingGroups[i].groupId : '';
                    initialApplyTime[g.groupNumber] = false;
                });
                setGroupMappings(initialMappings);
                setApplyTargetTime(initialApplyTime);
                setActiveStep(1);
            }
        } catch (e) {
            if (!axios.isCancel(e)) {
                console.error(e);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleProceedToReview = () => {
        if (!distribution) return;
        const assignments: Record<string, string> = {};
        props.existingRiders.forEach(r => {
            assignments[r.tagId] = r.group;
        });
        distribution.groups.forEach(g => {
            const targetGroupId = groupMappings[g.groupNumber];
            if (targetGroupId) {
                g.riders.forEach(dr => {
                    if (dr.tagId in assignments) {
                        assignments[dr.tagId] = targetGroupId;
                    }
                });
            }
        });
        setRiderGroups(assignments);
        setActiveStep(2);
    };

    const handleApply = () => {
        if (!distribution) return;
        const updatedRiders = props.existingRiders.map(r => ({
            ...r,
            group: riderGroups[r.tagId] !== undefined ? riderGroups[r.tagId] : r.group,
        }));
        let updatedGroups = [...props.existingGroups];
        distribution.groups.forEach(g => {
            const targetGroupId = groupMappings[g.groupNumber];
            if (targetGroupId && applyTargetTime[g.groupNumber]) {
                const ticks = parseTimeToTicks(g.slowestTime);
                updatedGroups = updatedGroups.map(eg =>
                    eg.groupId === targetGroupId ? { ...eg, targetTime: ticks } : eg
                );
            }
        });
        props.onApply(updatedRiders, updatedGroups);
    };

    const getBestTimeForRider = (tagId: string): string | undefined => {
        if (!distribution) return undefined;
        for (const g of distribution.groups) {
            const dr = g.riders.find(r => r.tagId === tagId);
            if (dr) return dr.bestTime;
        }
        return undefined;
    };

    const ridersForReview = showOnlyMismatched
        ? props.existingRiders.filter(r => {
            const original = r.group;
            const current = riderGroups[r.tagId];
            return current !== undefined && current !== original;
        })
        : props.existingRiders;

    const totalRiders = distribution
        ? distribution.groups.reduce((sum, g) => sum + g.riders.length, 0)
        : 0;

    return (
        <Dialog open={true} fullWidth maxWidth="md" aria-labelledby="distribution-dialog-title">
            <DialogTitle id="distribution-dialog-title">
                {t("distribution.dialogTitle")}
            </DialogTitle>
            <DialogContent>
                <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                    {steps.map(label => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {activeStep === 0 && (
                    <Box sx={{ pt: 1 }}>
                        <TextField
                            label={t("distribution.numberOfGroups")}
                            type="number"
                            value={numberOfGroups}
                            inputProps={{ min: 1, max: 20 }}
                            onChange={e => setNumberOfGroups(Math.max(1, parseInt(e.target.value) || 1))}
                            sx={{ mb: 2 }}
                        />
                        {distribution && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {format(t("distribution.proposalSummary"),
                                    distribution.groups.length.toString(),
                                    totalRiders.toString())}
                            </Typography>
                        )}
                    </Box>
                )}

                {activeStep === 1 && distribution && (
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t("distribution.proposedGroup")}</TableCell>
                                    <TableCell align="right">{t("distribution.fastestTime")}</TableCell>
                                    <TableCell align="right">{t("distribution.slowestTime")}</TableCell>
                                    <TableCell align="right">{t("distribution.riders")}</TableCell>
                                    <TableCell>{t("distribution.existingGroup")}</TableCell>
                                    <TableCell>{t("distribution.applyTargetTime")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {distribution.groups.map(g => (
                                    <TableRow key={g.groupNumber}>
                                        <TableCell>
                                            {format(t("distribution.groupNumber"), g.groupNumber.toString())}
                                        </TableCell>
                                        <TableCell align="right">{g.fastestTime}</TableCell>
                                        <TableCell align="right">{g.slowestTime}</TableCell>
                                        <TableCell align="right">{g.riders.length}</TableCell>
                                        <TableCell>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={groupMappings[g.groupNumber] ?? ''}
                                                    onChange={e => setGroupMappings(prev => ({
                                                        ...prev,
                                                        [g.groupNumber]: e.target.value as string
                                                    }))}
                                                >
                                                    <MenuItem value="">{t("distribution.noMapping")}</MenuItem>
                                                    {props.existingGroups.map(eg => (
                                                        <MenuItem key={eg.groupId} value={eg.groupId}>
                                                            {eg.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={applyTargetTime[g.groupNumber] ?? false}
                                                        onChange={e => setApplyTargetTime(prev => ({
                                                            ...prev,
                                                            [g.groupNumber]: e.target.checked
                                                        }))}
                                                        disabled={!groupMappings[g.groupNumber]}
                                                    />
                                                }
                                                label=""
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {activeStep === 2 && (
                    <>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showOnlyMismatched}
                                    onChange={e => setShowOnlyMismatched(e.target.checked)}
                                />
                            }
                            label={t("distribution.showMismatched")}
                            sx={{ mb: 2 }}
                        />
                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t("rider.name")}</TableCell>
                                        <TableCell>{t("rider.tag")}</TableCell>
                                        <TableCell>{t("distribution.bestTime")}</TableCell>
                                        <TableCell>{t("rider.group")}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {ridersForReview.map(r => (
                                        <TableRow key={r.tagId}>
                                            <TableCell>{r.name}</TableCell>
                                            <TableCell>{r.tagId}</TableCell>
                                            <TableCell>{getBestTimeForRider(r.tagId) ?? '-'}</TableCell>
                                            <TableCell>
                                                <FormControl fullWidth size="small">
                                                    <Select
                                                        value={riderGroups[r.tagId] ?? r.group}
                                                        onChange={e => setRiderGroups(prev => ({
                                                            ...prev,
                                                            [r.tagId]: e.target.value as string
                                                        }))}
                                                    >
                                                        <MenuItem value="">{t("distribution.noMapping")}</MenuItem>
                                                        {props.existingGroups.map(g => (
                                                            <MenuItem key={g.groupId} value={g.groupId}>
                                                                {g.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onCancel} color="primary">
                    {t("commonActions.cancel")}
                </Button>
                {activeStep === 0 && (
                    <Button
                        onClick={handleFetch}
                        color="primary"
                        variant="contained"
                        disabled={loading || numberOfGroups < 1}
                        startIcon={loading ? <CircularProgress size={16} /> : undefined}
                    >
                        {t("distribution.fetchProposal")}
                    </Button>
                )}
                {activeStep === 1 && (
                    <>
                        <Button onClick={() => setActiveStep(0)} color="primary">
                            {t("distribution.back")}
                        </Button>
                        <Button
                            onClick={handleProceedToReview}
                            color="primary"
                            variant="contained"
                        >
                            {t("distribution.next")}
                        </Button>
                    </>
                )}
                {activeStep === 2 && (
                    <>
                        <Button onClick={() => setActiveStep(1)} color="primary">
                            {t("distribution.back")}
                        </Button>
                        <Button
                            onClick={handleApply}
                            color="primary"
                            variant="contained"
                        >
                            {t("distribution.apply")}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default RidersDistributionDialog;
